import { ParsedUrlQuery } from 'querystring';
import { FilterMenuItem, FiltersCollection } from 'src/modules/form/field/filters/types';
import { omit } from 'lodash';

export interface LazyInitializer<T> {
  (): T;
}

// transforms a parsed query object into an object suitable to be passed as values for
// the FormFilters component
export const getFiltersFromQuery = <T extends FiltersCollection>(
  allFilters: T,
  query: ParsedUrlQuery
): T => {
  const selectedFilters: any = {};

  Object.keys(allFilters).forEach((filterName) => {
    const filterObjects = allFilters[filterName];

    // if the query provides values for this filter,
    // populate the selectedFilters object with the corresponding filter objects
    if (query[filterName as string]) {
      const queryFilterValues = (query[filterName as string] as string).split(',');

      // populate the selected filters array
      selectedFilters[filterName] = queryFilterValues.reduce<FilterMenuItem[]>(
        (selectedFiltersAccumulator, queryFilterValue) => {
          // find the corresponding filter object and add it to the currentFilters accumulator
          const filterObject = filterObjects.find((fo) => fo.value === queryFilterValue);
          if (filterObject) {
            selectedFiltersAccumulator.push(filterObject);
          }
          return selectedFiltersAccumulator;
        },
        []
      );
    } else {
      // query did not provide a filter, set current filter
      selectedFilters[filterName] = [];
    }
  });

  return selectedFilters as T;
};

// converts filters object to query object with filter values separated by comma
export const getQueryFromFilters = (currentQueryObject, filters): { [k: string]: string } => {
  const filterNames = Object.keys(filters);

  const newFiltersQuery = filterNames.reduce((queryObject, filterName) => {
    const filterObjects = filters[filterName];

    if (!filterObjects.length) {
      return queryObject;
    }

    return { ...queryObject, [filterName]: filterObjects.map((fo) => fo.value).join(',') };
  }, {});

  return {
    ...omit(currentQueryObject, filterNames),
    ...newFiltersQuery,
  };
};
